import http from '@utils/http';
import { create } from 'zustand';
import { ENV } from '@configs/env';
import { persist } from 'zustand/middleware';
import { encodeIfPresent } from '@utils/tenantCrypto';

export const useAuthStore = create(
    persist(
        (set, get) => ({
            user: null,
            role: null,
            permissions: [],
            isAuthenticated: false,
            accessToken: null,
            refreshToken: null,
            isRefreshing: false,

            onboardTheClient: async (data) => {
                const currentUser = get().user;
                try {
                    const response = await http.patch('/user/onboard-the-client', data);
                    if (response?.data?.success) {
                        set({
                            user: {
                                ...currentUser,
                                isOnboarded: true
                            }
                        });
                    }
                    return { message: response?.data?.data?.message || response?.data?.message };
                } catch (error) {
                    const errorMessage =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        'Onboard client failed';
                    throw new Error(errorMessage);
                }
            },

            updateUserStore: async (userData) => {
                const currentUser = get().user;
                const allowedFields = ['name', 'email', 'phone'];
                const updatedUserData = {};

                for (const field of allowedFields) {
                    const newValue = userData[field];
                    const currentValue = currentUser[field];

                    const isFieldEmpty =
                        newValue === undefined || newValue === null || newValue === '';
                    const isNewButEmpty = currentValue === undefined && isFieldEmpty;
                    const isChanged = newValue && newValue !== currentValue;

                    if (!isNewButEmpty && isChanged) {
                        updatedUserData[field] = newValue;
                    }
                }

                if (Object.keys(updatedUserData).length === 0) {
                    return { status: 304, message: 'No changes detected' };
                }

                try {
                    const response = await http.patch('/user/update/profile', updatedUserData);
                    set((state) => ({
                        user: {
                            ...state.user,
                            ...updatedUserData
                        }
                    }));
                    return { message: response?.data?.data?.message || response?.data?.message };
                } catch (error) {
                    const errorMessage =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        'Update user failed';
                    throw new Error(errorMessage);
                }
            },

            login: async (email, password) => {
                try {
                    const encodedPassword = await encodeIfPresent(password);
                    const response = await http.post('/auth/login', {
                        email,
                        password: encodedPassword
                    });
                    const { data } = response.data;
                    set({
                        user: data.user,
                        role: data.user.role,
                        permissions: data.user.permissions || [],
                        isAuthenticated: true,
                        accessToken: data.accessToken,
                        refreshToken: data.refreshToken,
                        isRefreshing: false
                    });
                    return response;
                } catch (error) {
                    const errorMessage =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        'Login failed';
                    throw new Error(errorMessage);
                }
            },

            register: async (formData) => {
                try {
                    const response = await http.post('/auth/register', formData);
                    const { data } = response.data;

                    if (response.status === 200 && response.data.message !== '') {
                        return {
                            routeToLogin: true,
                            message: response.data.message
                        };
                    }
                    set({
                        user: data.user,
                        role: data.user.role,
                        permissions: data.user.permissions || [],
                        isAuthenticated: true,
                        accessToken: data.accessToken,
                        refreshToken: data.refreshToken,
                        isRefreshing: false
                    });
                    return response;
                } catch (error) {
                    const errorMessage =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        'Registration failed';
                    throw new Error(errorMessage);
                }
            },

            logout: async () => {
                try {
                    const refreshTokenValue = get().refreshToken;
                    if (refreshTokenValue) {
                        await http.post(
                            '/auth/logout',
                            {},
                            {
                                headers: { 'x-refresh-token': refreshTokenValue }
                            }
                        );
                    }
                } catch {
                    // ignore
                }
                set({
                    user: null,
                    role: null,
                    permissions: [],
                    isAuthenticated: false,
                    accessToken: null,
                    refreshToken: null,
                    isRefreshing: false
                });
            },

            hardLogout: () => {
                set({
                    user: null,
                    role: null,
                    permissions: [],
                    isAuthenticated: false,
                    accessToken: null,
                    refreshToken: null,
                    isRefreshing: false
                });
            }
        }),
        {
            name:
                ENV.NODE_ENV === 'development'
                    ? 'edocs-auth-store-local'
                    : 'edocs-auth-store-live',
            getStorage: () => localStorage,
            partialize: (state) => ({
                user: state.user,
                role: state.role,
                permissions: state.permissions,
                isAuthenticated: state.isAuthenticated,
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                isRefreshing: state.isRefreshing
            })
        }
    )
);
